import axios from "axios";
import { toast } from "react-toastify";
import config from "./config";
/**
 * Extract user-friendly error message from axios error
 * @param {Error} error - Axios error object
 * @returns {string} - User-friendly error message
 */
export const getErrorMessage = (error) => {
  // Network error (no response from server)
  if (!error.response) {
    if (error.request) {
      return "Network error. Please check your connection.";
    }
    return error.message || "An unexpected error occurred.";
  }
  // HTTP error with response
  const { status, data } = error.response;
  // Handle different status codes
  switch (status) {
    case 400:
      return data || "Bad request. Please check your input.";
    case 401:
      return "Unauthorized. Please login again.";
    case 403:
      return "Access forbidden.";
    case 404:
      return data || "Resource not found.";
    case 409:
      return data || "Conflict. Resource already exists.";
    case 422:
      return data || "Validation error. Please check your input.";
    case 500:
      return "Server error. Please try again later.";
    case 503:
      return "Service unavailable. Please try again later.";
    default:
      return data || `Error ${status}. Please try again.`;
  }
};
/**
 * Handle axios error and show toast notification
 * @param {Error} error - Axios error object
 * @param {string} fallbackMessage - Optional fallback message
 */
export const handleError = (error, fallbackMessage) => {
  console.error("API Error:", error);
  const message = getErrorMessage(error);
  toast.error(fallbackMessage || message);
};
/**
 * Axios instance with default config
 */
const axiosInstance = axios.create({
  baseURL: config.callUrl,
  timeout: config.apiTimeout || 30000,
  withCredentials: true, // CRITICAL: Enable sending cookies
  headers: {
    "Content-Type": "application/json",
  },
});
/**
 * Request interceptor - add auth token if exists
 */
axiosInstance.interceptors.request.use(
  (config) => {
    // Copy Authorization header from axios defaults (set by CurrentUserContext)
    const authHeader = axios.defaults.headers.common["Authorization"];
    if (authHeader) {
      config.headers["Authorization"] = authHeader;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);
/**
 * Response interceptor - handle errors globally
 */
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // You can handle specific errors globally here
    // For example, redirect to login on 401
    if (error.response?.status === 401) {
      // Optional: redirect to login or clear auth
      // window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);
/**
 * GET request wrapper
 * @param {string} url - API endpoint
 * @param {object} config - Axios config
 * @returns {Promise}
 */
export const get = (url, config = {}) => {
  return axiosInstance.get(url, config);
};
/**
 * POST request wrapper
 * @param {string} url - API endpoint
 * @param {object} data - Request body
 * @param {object} config - Axios config
 * @returns {Promise}
 */
export const post = (url, data = {}, config = {}) => {
  return axiosInstance.post(url, data, config);
};
/**
 * PUT request wrapper
 * @param {string} url - API endpoint
 * @param {object} data - Request body
 * @param {object} config - Axios config
 * @returns {Promise}
 */
export const put = (url, data = {}, config = {}) => {
  return axiosInstance.put(url, data, config);
};
/**
 * DELETE request wrapper
 * @param {string} url - API endpoint
 * @param {object} config - Axios config
 * @returns {Promise}
 */
export const del = (url, config = {}) => {
  return axiosInstance.delete(url, config);
};
/**
 * PATCH request wrapper
 * @param {string} url - API endpoint
 * @param {object} data - Request body
 * @param {object} config - Axios config
 * @returns {Promise}
 */
export const patch = (url, data = {}, config = {}) => {
  return axiosInstance.patch(url, data, config);
};
export default {
  get,
  post,
  put,
  delete: del,
  patch,
  handleError,
  getErrorMessage,
  axiosInstance,
};
